import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { calculateOptimalSpans } from "./gridCalculator";
import type { BaseTile, TileWithPosition } from "./types";

export interface BentoProps {
  accentColor?: string;
  maxRows?: number;
  tiles: BaseTile[];
  title: string;
}

export const Bento = ({
  accentColor = "#3F107B",
  maxRows: maxRowsProp = 8,
  tiles,
  title,
}: BentoProps) => {
  const [expandedTiles, setExpandedTiles] = useState<TileWithPosition[]>([]);
  const [maxRows, setMaxRows] = useState(maxRowsProp);

  const enabledTiles = useMemo(
    () => tiles.filter(tile => tile.enabled),
    [tiles],
  );

  useEffect(() => {
    const optimized = calculateOptimalSpans(enabledTiles);
    setExpandedTiles(optimized);

    const maxRowEnd = Math.max(...optimized.map(t => t.row + t.rowSpan), 0);
    setMaxRows(Math.min(maxRowEnd, maxRowsProp));
  }, [enabledTiles, maxRowsProp]);

  const containerMaxHeight = `${maxRows * 140 + (maxRows - 1) * 16}px`;

  return (
    <div
      className="min-h-screen relative overflow-hidden pt-10"
      style={{ "--accent-color": accentColor } as React.CSSProperties}
    >
      <div className="absolute top-1/4 left-1/4 w-[908px] h-[494px] rounded-full blur-[250px] opacity-40 bg-[var(--accent-color)]" />
      <div className="absolute bottom-1/4 right-1/4 w-[908px] h-[494px] rounded-full blur-[150px] opacity-30 bg-[var(--accent-color)]" />

      <div className="absolute inset-0 [background-image:linear-gradient(rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.02)_1px,transparent_1px)] [background-size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

      <div className="container mx-auto px-4 py-12 relative z-10">
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-12 text-center">
          {title}
        </h1>

        <div
          className="grid grid-cols-3 auto-rows-[140px] gap-4 grid-flow-dense"
          style={{ maxHeight: containerMaxHeight }}
        >
          <AnimatePresence mode="sync">
            {expandedTiles.map((tile, index) => (
              <motion.div
                key={tile.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                  layout: { duration: 0.4 },
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  gridColumn: `span ${tile.colSpan}`,
                  gridRow: `span ${tile.rowSpan}`,
                }}
              >
                <div className="bg-[#0e1628]/40 backdrop-blur-md border border-white/10 hover:border-[var(--accent-color)] text-white p-6 rounded-2xl flex flex-col justify-between h-full transition-all duration-300">
                  {tile.children}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
